import React, { useEffect, useMemo, useRef, useState } from "react";
import { CustomInput } from "components/custom-form-input/CustomInputs";
import renderLatex from "components/courses/add-new-course/steps/quiz-genaration/RenderLatex";
import { Button, Alert, Badge, Form } from "react-bootstrap";
import { Edit, Save, X, CheckCircle, Add, Delete } from "@mui/icons-material";

export const EditableQnADisplay = ({ responseData = [] }) => {
  const lastTempId = useRef(0);
  const genId = () => `temp_${++lastTempId.current}`;

  const [grouped, setGrouped] = useState({});
  const [editing, setEditing] = useState(null);
  const [original, setOriginal] = useState(null);

  // -------------------------------------------------
  // 1. Stable key – only rebuild when content changes
  // -------------------------------------------------
  const dataKey = useMemo(() => JSON.stringify(responseData), [responseData]);

  // -------------------------------------------------
  // 2. Build grouped structure **once**
  // -------------------------------------------------
useEffect(() => {
  const g = {};
  responseData.forEach((q) => {
    const key = `${q.type}_${q.difficulty || "medium"}`;
    if (!g[key]) g[key] = [];

    // Safely create options array only for MCQ and True/False
    const optionsArray =
      (q.type === "mcq" || q.type === "true_or_false") && Array.isArray(q.options) && q.options.length > 0
        ? q.options.map((opt, idx) => ({
            value: String.fromCharCode(97 + idx), // 'a', 'b', 'c', 'd'
            answer: typeof opt === 'object' ? opt.text || opt.answer : opt || "",
          }))
        : q.type === "true_or_false" 
          ? [
              { value: 'a', answer: 'True' },
              { value: 'b', answer: 'False' }
            ]
          : [];

    g[key].push({
      ...q,
      id: q.id || genId(),
      options: optionsArray, // Always defined as array
    });
  });
  setGrouped(g);
}, [dataKey]);

  // -------------------------------------------------
  // 3. Edit helpers
  // -------------------------------------------------
  const startEdit = (q) => {
    if (editing && editing.id !== q.id) cancelEdit();
    const key = `${q.type}_${q.difficulty || "medium"}`;
    const toEdit = grouped[key]?.find((x) => x.id === q.id);
    if (!toEdit) return;
    setEditing(toEdit);
    setOriginal({ ...toEdit });
  };

  const cancelEdit = () => {
    if (editing && original) {
      const key = `${editing.type}_${editing.difficulty || "medium"}`;
      setGrouped((prev) => ({
        ...prev,
        [key]: prev[key].map((x) => (x.id === editing.id ? original : x)),
      }));
    }
    setEditing(null);
    setOriginal(null);
  };

  const saveEdit = () => {
    if (!editing) return;
    const key = `${editing.type}_${editing.difficulty || "medium"}`;
    const cur = grouped[key]?.find((x) => x.id === editing.id);
    if (!cur) return;

    if (!cur.question?.trim()) return alert("Question required");
    if (cur.type === "mcq" && cur.options?.some((o) => !o.answer?.trim()))
      return alert("All options required");
    if (!cur.correctAnswer) return alert("Answer required");

    setEditing(null);
    setOriginal(null);
  };

  const setField = (field, value) => {
    if (!editing) return;
    const key = `${editing.type}_${editing.difficulty || "medium"}`;
    setGrouped((prev) => ({
      ...prev,
      [key]: prev[key].map((x) =>
        x.id === editing.id
          ? {
              ...x,
              [field]:
                field === "correctAnswer" && editing.type === "fill_blanks"
                  ? isNaN(Number(value))
                    ? ""
                    : Number(value)
                  : value,
            }
          : x
      ),
    }));
  };

  // Update option for MCQs during editing
  const setOption = (options, opt, e) => {
    if (!editing) return;
    const key = `${editing.type}_${editing.difficulty || "medium"}`;
    setGrouped((prev) => ({
      ...prev,
      [key]: prev[key].map((x) =>
        x.id === editing.id
          ? {
              ...x,
              options: options.map((o) =>
                o.value === opt.value ? { ...o, answer: e.target.value } : o
              ),
            }
          : x
      ),
    }));
  };
  // Update a single rubric criterion field during editing
  const setRubricField = (idx, field, value) => {
    if (!editing) return;
    const key = `${editing.type}_${editing.difficulty || "medium"}`;
    setGrouped((prev) => ({
      ...prev,
      [key]: prev[key].map((x) =>
        x.id === editing.id
          ? {
              ...x,
              rubric: (x.rubric || []).map((r, i) =>
                i === idx
                  ? { ...r, [field]: field === "marks" ? Number(value) : value }
                  : r
              ),
            }
          : x
      ),
    }));
  };

  const addRubricCriterion = () => {
    if (!editing) return;
    const key = `${editing.type}_${editing.difficulty || "medium"}`;
    setGrouped((prev) => ({
      ...prev,
      [key]: prev[key].map((x) =>
        x.id === editing.id
          ? {
              ...x,
              rubric: [
                ...(x.rubric || []),
                { criterion: "", description: "", marks: 0, partial_credit_guidelines: "" },
              ],
            }
          : x
      ),
    }));
  };

  const removeRubricCriterion = (idx) => {
    if (!editing) return;
    const key = `${editing.type}_${editing.difficulty || "medium"}`;
    setGrouped((prev) => ({
      ...prev,
      [key]: prev[key].map((x) =>
        x.id === editing.id
          ? { ...x, rubric: (x.rubric || []).filter((_, i) => i !== idx) }
          : x
      ),
    }));
  };

  // -------------------------------------------------
  // 4. Render one group
  // -------------------------------------------------
  const renderGroup = (key) => {
    const list = grouped[key] || [];
    const type = key.split("_")[0];
    const labels = {
        mcq: "Multiple Choice",
        fill_in_the_blanks: "Fill in the Blanks",
        true_or_false: "True / False",
        very_short_answer: "Very Short Answer",
        short_answer: "Short Answer",
        medium_answer: "Medium Answer",
        long_answer: "Long Answer",
        essay_answer: "Essay Answer",
        programming: "Programming"
    };
    const title = labels[type] || type.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

    return (
      <div key={key} className="mb-5 border-0">
        <h4 className="mb-4 d-flex align-items-center gap-2">
            <span style={{ width: '4px', height: '24px', backgroundColor: '#23479f', borderRadius: '4px' }}></span>
            {title} Questions
            <Badge bg="light" text="dark" pill className="ms-2 border" style={{ fontSize: '0.6em' }}>
                {list.length}
            </Badge>
        </h4>

        <ul className="list-group list-group-flush" style={{ gap: '1.5rem' }}>
          {list.map((q, i) => {
            const isEdit = editing?.id === q.id;

            return (
              <li key={q.id} className="list-group-item border rounded-3 p-4 shadow-sm" style={{ backgroundColor: '#fff' }}>
                <div className="d-flex justify-content-between">
                  <div className="flex-grow-1">
                    {/* METADATA BADGES */}
                    <div className="d-flex flex-wrap gap-2 mb-3">
                        {q.taxonomy && (
                            <Badge bg="primary-subtle" className="text-primary text-uppercase px-2 py-1" style={{ fontSize: '0.7em', fontWeight: '700' }}>
                                {q.taxonomy}
                            </Badge>
                        )}
                        {q.marks && (
                            <Badge bg="success-subtle" className="text-success px-2 py-1" style={{ fontSize: '0.7em', fontWeight: '700' }}>
                                {q.marks} {q.marks === 1 ? 'Mark' : 'Marks'}
                            </Badge>
                        )}
                        {q.courseOutcome && (
                            <Badge bg="info-subtle" className="text-info px-2 py-1" style={{ fontSize: '0.7em', fontWeight: '700' }}>
                                {q.courseOutcome}
                            </Badge>
                        )}
                    </div>

                    {/* CONTENT AREA */}
                    {isEdit ? (
                      <div className="d-flex flex-column gap-3">
                        <CustomInput
                          label="Question"
                          value={q.question || ""}
                          onChange={(e) => setField("question", e.target.value)}
                          placeholder="Enter question"
                          type="textarea"
                          rows={3}
                        />

                        {(q.type === "mcq" || q.type === "true_or_false") && q.options && Array.isArray(q.options) && (
                          <div className="ms-3 border-start ps-3 py-1">
                            <div className="text-muted small text-uppercase fw-bold mb-2">Options</div>
                            {q.options.map((opt, idx) => (
                              <div key={opt.value} className="d-flex align-items-center gap-2 mb-2">
                                <div className="flex-grow-1">
                                  <CustomInput
                                    type="text"
                                    label={`Option ${opt.value.toUpperCase()}`}
                                    value={opt.answer || ""}
                                    onChange={(e) => setOption(q.options, opt, e)}
                                    placeholder={`Enter option ${opt.value.toUpperCase()}`}
                                  />
                                </div>
                                <Form.Check
                                  type="radio"
                                  name={`correct-${q.id}`}
                                  checked={q.correctAnswer === opt.answer || q.correctAnswer === opt.value}
                                  onChange={() => setField("correctAnswer", opt.answer)}
                                  className="mt-4"
                                />
                              </div>
                            ))}
                          </div>
                        )}

                        <div className="row g-3">
                          <div className="col-md-9">
                            <CustomInput
                              label="Correct Answer"
                              value={q.correctAnswer ?? ""}
                              onChange={(e) => setField("correctAnswer", e.target.value)}
                              placeholder="Enter correct answer"
                            />
                          </div>
                          <div className="col-md-3">
                            <CustomInput
                              type="number"
                              label="Marks"
                              value={q.marks || 1}
                              onChange={(e) => setField("marks", Number(e.target.value))}
                              placeholder="Marks"
                            />
                          </div>
                        </div>

                        <CustomInput
                          label="Explanation"
                          value={q.explanation || ""}
                          onChange={(e) => setField("explanation", e.target.value)}
                          placeholder="Enter explanation"
                          type="textarea"
                          rows={2}
                        />

                        {Array.isArray(q.rubric) && (
                          <div className="border-top pt-3">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <div className="text-muted small text-uppercase fw-bold">Marking Criteria</div>
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={addRubricCriterion}
                                className="d-flex align-items-center gap-1"
                              >
                                <Add fontSize="small" /> Add Criterion
                              </Button>
                            </div>

                            {q.rubric.length === 0 && (
                              <div className="text-muted small mb-2">No rubric criteria yet.</div>
                            )}

                            {q.rubric.map((r, idx) => (
                              <div key={idx} className="border rounded-3 p-3 mb-2 bg-light">
                                <div className="d-flex justify-content-between align-items-start gap-2">
                                  <div className="flex-grow-1">
                                    <div className="row g-3">
                                      <div className="col-md-9">
                                        <CustomInput
                                          label="Criterion"
                                          value={r.criterion || ""}
                                          onChange={(e) => setRubricField(idx, "criterion", e.target.value)}
                                          placeholder="Criterion name"
                                        />
                                      </div>
                                      <div className="col-md-3">
                                        <CustomInput
                                          type="number"
                                          label="Marks"
                                          value={r.marks ?? 0}
                                          onChange={(e) => setRubricField(idx, "marks", e.target.value)}
                                          placeholder="Marks"
                                        />
                                      </div>
                                    </div>
                                    <CustomInput
                                      label="Description"
                                      value={r.description || ""}
                                      onChange={(e) => setRubricField(idx, "description", e.target.value)}
                                      placeholder="Describe what this criterion evaluates"
                                      type="textarea"
                                      rows={2}
                                    />
                                    <CustomInput
                                      label="Partial Credit Guidelines"
                                      value={r.partial_credit_guidelines || ""}
                                      onChange={(e) => setRubricField(idx, "partial_credit_guidelines", e.target.value)}
                                      placeholder="Explain how partial credit should be awarded"
                                      type="textarea"
                                      rows={2}
                                    />
                                  </div>
                                  <Button
                                    onClick={() => removeRubricCriterion(idx)}
                                    variant="outline-danger"
                                    className="rounded-circle"
                                    style={{ width: '32px', height: '32px', padding: 0, flexShrink: 0 }}
                                  >
                                    <Delete fontSize="small" />
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ) : (
                      <>
                        <h5 className="mb-4 fw-bold text-dark" style={{ lineHeight: '1.5' }}>
                          Q{i + 1}:{" "}
                          <span
                            dangerouslySetInnerHTML={{
                              __html: renderLatex(q.question || ""),
                            }}
                          />
                        </h5>

                        {/* OPTIONS (Vertical List - Old Style) */}
                        {(q.type === "mcq" || q.type === "true_or_false") && q.options && Array.isArray(q.options) && (
                          <div className="mb-4 ms-4">
                             <ul className="list-group list-group-flush">
                                {q.options.map((opt, idx) => {
                                  const isCorrect = opt.answer === q.correctAnswer || 
                                                    opt.value === q.correctAnswer ||
                                                    (q.type === "true_or_false" && String(opt.answer).toLowerCase() === String(q.correctAnswer).toLowerCase());
                                  
                                  return (
                                    <li key={opt.value} className={`list-group-item border-0 py-2 d-flex align-items-center ${isCorrect ? 'text-primary fw-bold' : ''}`}>
                                      <span className="me-3 text-muted" style={{ width: '20px' }}>{opt.value.toUpperCase()}.</span>
                                      <span
                                        dangerouslySetInnerHTML={{
                                          __html: renderLatex(opt.answer || ""),
                                        }}
                                      />
                                      {isCorrect && <CheckCircle className="ms-2 text-primary" style={{ fontSize: '1rem' }} />}
                                    </li>
                                  );
                                })}
                             </ul>
                          </div>
                        )}

                        {/* ANSWER PREVIEW */}
                        <div className="mb-4">
                            <div
                              className="p-3 rounded-3"
                              style={{
                                backgroundColor: "#f8f9fa",
                                borderLeft: "4px solid #23479f",
                              }}
                            >
                              <div className="text-muted small text-uppercase fw-bold mb-1">Correct Answer</div>
                              <div className="fw-bold text-primary">{q.correctAnswer ?? ""}</div>
                            </div>
                        </div>

                        {/* EXPLANATION & TOPICS */}
                        <div className="mt-3">
                            {q.explanation && (
                                <div className="mb-3">
                                    <div className="text-muted small text-uppercase fw-bold mb-1">Explanation</div>
                                    <div className="text-secondary small" dangerouslySetInnerHTML={{ __html: renderLatex(q.explanation) }} />
                                </div>
                            )}
                            
                            {q.topics && q.topics.length > 0 && (
                                <div className="mb-3">
                                    <div className="text-muted small text-uppercase fw-bold mb-2">Topics Covered</div>
                                    <div className="d-flex flex-wrap gap-1">
                                        {q.topics.map((topic, tidx) => (
                                            <Badge key={tidx} bg="secondary-subtle" className="text-secondary border-0" pill style={{ fontSize: '0.65em' }}>
                                                {topic}
                                            </Badge>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {Array.isArray(q.rubric) && q.rubric.length > 0 && (
                                <div>
                                    <div className="text-muted small text-uppercase fw-bold mb-2">Rubric</div>
                                    <div className="table-responsive">
                                        <table className="table table-sm table-bordered mb-0" style={{ fontSize: '0.85em' }}>
                                            <thead className="table-light">
                                                <tr>
                                                    <th>Criterion</th>
                                                    <th>Description</th>
                                                    <th style={{ width: '70px' }}>Marks</th>
                                                    <th>Partial Credit Guidelines</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {q.rubric.map((r, ridx) => (
                                                    <tr key={ridx}>
                                                        <td className="fw-semibold">{r.criterion}</td>
                                                        <td>{r.description}</td>
                                                        <td>{r.marks}</td>
                                                        <td>{r.partial_credit_guidelines}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </div>
                      </>
                    )}
                  </div>

                  {/* ACTIONS */}
                  <div className="d-flex flex-column align-items-end ms-3">
                    {isEdit ? (
                      <>
                        <Button
                          onClick={saveEdit}
                          variant="success"
                          className="rounded-circle mb-2"
                          style={{ width: '38px', height: '38px', padding: 0 }}
                        >
                          <Save fontSize="small" />
                        </Button>
                        <Button
                          onClick={cancelEdit}
                          variant="danger"
                          className="rounded-circle mb-2"
                          style={{ width: '38px', height: '38px', padding: 0 }}
                        >
                          <X fontSize="small" />
                        </Button>
                      </>
                    ) : (
                      <Button
                        onClick={() => startEdit(q)}
                        variant="outline-primary"
                        className="rounded-circle"
                        style={{ width: '38px', height: '38px', padding: 0 }}
                      >
                        <Edit fontSize="small" />
                      </Button>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    );
  };


  const renderAll = () => Object.keys(grouped).map(renderGroup);

  // -------------------------------------------------
  // 5. Guard – never show “No questions” when data exists
  // -------------------------------------------------
  if (!responseData || responseData.length === 0) {
    return <Alert variant="info">No questions generated yet.</Alert>;
  }

  return <div>{renderAll()}</div>;
};